import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Table, Spinner, Alert } from "react-bootstrap";
import { fetchPODImages, uploadPODImage, deletePODImage } from "../CommandesFournisseurs/DataService";
import { toast } from "react-toastify";
import moment from "moment";
import { urlPdf } from "../../../utils/ApiConfig";

function PODImages({ commandeId, readOnly = false }) {
  const [podImages, setPodImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Charger les images POD
  const loadPODImages = async () => {
    setLoading(true);
    try {
      const response = await fetchPODImages(commandeId);
      console.log("POD Images response:", response);
      // fetchPODImages retourne déjà response.data, donc response contient directement { images: [...] }
      setPodImages(response.images || []);
    } catch (error) {
      console.error("Error loading POD images:", error);
      toast.error("Erreur lors du chargement des images POD");
    } finally {
      setLoading(false);
    }
  };

  // Uploader une nouvelle image
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.warning("Veuillez sélectionner une image");
      return;
    }

    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Format de fichier non autorisé. Formats acceptés : JPG, JPEG, PNG, GIF");
      return;
    }

    // Vérifier la taille du fichier (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("La taille du fichier ne doit pas dépasser 5 Mo");
      return;
    }

    setUploading(true);
    try {
      await uploadPODImage(commandeId, selectedFile);
      toast.success("Image POD ajoutée avec succès");
      setSelectedFile(null);
      // Réinitialiser l'input file
      document.getElementById('podFileInput').value = '';
      // Recharger la liste
      await loadPODImages();
    } catch (error) {
      console.error("Error uploading POD image:", error);
      toast.error("Erreur lors de l'ajout de l'image POD");
    } finally {
      setUploading(false);
    }
  };

  // Supprimer une image
  const handleDelete = async (imageId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette image POD ?")) {
      return;
    }

    try {
      await deletePODImage(commandeId, imageId);
      toast.success("Image POD supprimée avec succès");
      await loadPODImages();
    } catch (error) {
      console.error("Error deleting POD image:", error);
      toast.error("Erreur lors de la suppression de l'image POD");
    }
  };

  // Charger les images au montage du composant
  useEffect(() => {
    if (commandeId) {
      loadPODImages();
    }
  }, [commandeId]);

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="pb-3">Images de Preuve de Livraison (POD)</Card.Title>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </div>
        ) : (
          <>
            {/* Liste des images existantes */}
            {podImages.length > 0 ? (
              <Table striped bordered hover responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Date d'upload</th>
                    <th>Uploadé par</th>
                    {!readOnly && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {podImages.map((pod) => {
                    // Utiliser image_url si disponible (URL complète du backend), sinon construire l'URL
                    const imageUrl = pod.image_url || `${urlPdf}/${pod.image_path}`;
                    // Formater la date d'upload
                    const uploadDate = pod.uploaded_at || (pod.created_at ? moment(pod.created_at).format("DD-MM-YYYY HH:mm") : "-");
                    // Nom de l'uploader
                    const uploaderName = pod.uploaded_by?.nom && pod.uploaded_by?.prenom
                      ? `${pod.uploaded_by.nom} ${pod.uploaded_by.prenom}`
                      : (pod.uploaded_by || "-");

                    return (
                    <tr key={pod.id}>
                      <td>
                        <a
                          href={imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={imageUrl}
                            alt="POD"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "100px",
                              objectFit: "cover",
                              cursor: "pointer"
                            }}
                          />
                        </a>
                      </td>
                      <td>{uploadDate}</td>
                      <td>{uploaderName}</td>
                      {!readOnly && (
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(pod.id)}
                          >
                            Supprimer
                          </Button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">Aucune image POD disponible</Alert>
            )}

            {/* Formulaire d'upload (seulement si non readOnly) */}
            {!readOnly && (
              <Row className="mt-3">
                <Col md={8}>
                  <input
                    id="podFileInput"
                    type="file"
                    className="form-control"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    disabled={uploading}
                  />
                  <small className="text-muted">
                    Formats acceptés : JPG, JPEG, PNG, GIF. Taille max : 5 Mo
                  </small>
                </Col>
                <Col md={4}>
                  <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile}
                  >
                    {uploading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Upload en cours...
                      </>
                    ) : (
                      "Ajouter une image POD"
                    )}
                  </Button>
                </Col>
              </Row>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default PODImages;
